import Footer from '@bridebook/web/app-basic/components/footer/footer';
import Head from '@bridebook/web/app-basic/components/head/head';
import { TopBar } from '@bridebook/web/app-basic/components/header';
import { setAlternateLinks, setCanonicalLink } from '@bridebook/web/app-basic/lib/app/app-slice';
import {
  basicGSSPWrapper,
  EdgeCacheTTL,
} from '@bridebook/web/app-basic/lib/get-basic-server-side-props-wrapper';
import { ToggleEnquiryFormButtonAura } from '@bridebook/web/app-basic/components/supplier/toggle-enquiry-form-button-aura';
import { saveSupplier } from '@bridebook/web/app-basic/lib/supplier/supplier-slice';
import { NextPage } from 'next';
import { Link } from '@bridebook/web/app-shared/components/link/link';
import { isEmpty } from 'ramda';
import { useTranslation } from 'next-i18next';
import { ISupplier, ISupplier_SeoVideo } from '@bridebook/models/source/models/Suppliers.types';
import { getSearchUrl, getSupplierUrl, mapUrlSlugToSupplierType } from '@bridebook/toolbox/src';
import { HttpStatusCode } from '@bridebook/toolbox/src/api/http-status';
import { SentryMinimal } from '@bridebook/toolbox/src/sentry';
import { decodePublicSupplierId } from '@bridebook/toolbox/src/hashids';
import type { UrlFriendlySlug } from '@bridebook/toolbox/src/types';
import { Button } from '@bridebook/aura';
import { ChevronLeft } from '@bridebook/aura/src/icons';
import { useMarket } from '@bridebook/web/app-shared/lib/i18n/hooks/use-market';
import { fetchSupplierBasicData } from '@bridebook/web/app-shared/lib/supplier/fetchers';
import { fetchSupplierError } from '@bridebook/web/app-basic/lib/supplier/supplier-details-slice';
import LdJsonScript from '@bridebook/web/app-shared/components/ld-json-script/ld-json-script';
import { translateRecursive } from '@bridebook/web/app-shared/lib/utils/translate-recursive';
import {
  generateBreadcrumbListMicrodata,
  generateVideoObjectMicrodata,
} from '@bridebook/web/lib/utils/microdata-schema-generators';
import { SupplierVideoBody } from '@bridebook/web/app-shared/components/supplier/video/supplier-video-body';
import { SupplierVideoBreadcrumbs } from '@bridebook/web/app-shared/components/supplier/video/supplier-video-breadcrumbs';
import { getBreadcrumbsWithHome } from '@bridebook/web/app-shared/lib/utils';
import { buildUISupplier } from '@bridebook/web/lib/shortlist/utils';
import {
  getSupplierVideoEmbedUrl,
  getSupplierVideoId,
  getSupplierVideoThumbnail,
} from '@bridebook/web/lib/supplier/video';
import { getLocalisedCanonical } from '@bridebook/web/lib/utils/i18n-routing/get-localised-canonical';
import { AppType } from '@bridebook/web/pages/_app';

type IVideoProps = Required<Omit<ISupplier_SeoVideo, 'youtubeUrl'>>;

interface IProps {
  canonicalUrl: string;
  profileUrl: string;
  name: string;
  location: string;
  descriptionShort: string;
  video: IVideoProps;
}

const VideoPage: NextPage<IProps> = ({
  canonicalUrl,
  profileUrl,
  name,
  location,
  descriptionShort,
  video,
}) => {
  const market = useMarket();
  const { t } = useTranslation('supplier');

  const venue = location ? t('video.meta.venueWithLocation', { name, location }) : name;
  const metaDescription = t('video.meta.description', { venue });

  const title = t('video.venue.heading', { name });
  const videoTitle = video.title || title;
  const videoDescription = video.description || descriptionShort;
  const embedUrl = getSupplierVideoEmbedUrl(video.videoId);

  const breadcrumbs = getBreadcrumbsWithHome(undefined, [
    {
      id: 'venues',
      name: t('common:navigation.weddingVenues'),
      path: getSearchUrl({ slug: 'venues', as: true, fallbackToDefaultArea: false }),
    },
    { id: 'venue', name, path: profileUrl },
    { id: 'video', name: t('video.tag'), path: `${profileUrl}/video` },
  ]);

  const breadcrumbMicrodata = generateBreadcrumbListMicrodata(
    breadcrumbs.map((item) => ({
      ...item,
      path: item.path ? getLocalisedCanonical(market, item.path) : item.path,
    })),
  );

  const videoObjectMicrodata = generateVideoObjectMicrodata({
    name: videoTitle,
    description: videoDescription,
    videoId: video.videoId,
    uploadDate: video.uploadDate,
    duration: video.duration,
    thumbnailUrl: video.thumbnailUrl,
  });

  const breadcrumbsNav = (
    <SupplierVideoBreadcrumbs breadcrumbs={breadcrumbs} LinkComponent={Link} />
  );

  const actions = (
    <>
      <Button variant="secondary" size="lg" icon={ChevronLeft} asChild className="w-full sm:w-auto">
        <Link href={profileUrl}>{t('video.viewVenue')}</Link>
      </Button>
      <ToggleEnquiryFormButtonAura
        contactMethod="supplierProfile_topButton"
        size="lg"
        className="w-full sm:w-auto"
      />
    </>
  );

  return (
    <>
      <Head
        title={title}
        ogTitle={title}
        description={metaDescription}
        ogImage={video.thumbnailUrl}
        ogType="video.other"
        robots="index, follow"
        isIndexable>
        <meta property="og:description" content={metaDescription} />
        <meta property="og:video" content={embedUrl} />
        <meta property="twitter:description" content={metaDescription} />
        <meta property="twitter:url" content={canonicalUrl} />
      </Head>

      {videoObjectMicrodata && <LdJsonScript data={videoObjectMicrodata} />}
      {breadcrumbMicrodata && <LdJsonScript data={breadcrumbMicrodata} />}

      <TopBar />

      <SupplierVideoBody
        heading={title}
        descriptionShort={descriptionShort}
        embedUrl={embedUrl}
        videoTitle={videoTitle}
        videoDescription={video.description}
        breadcrumbs={breadcrumbsNav}
        actions={actions}
      />

      <Footer />
    </>
  );
};

export const getServerSideProps = basicGSSPWrapper<IProps>(
  ['supplier', 'supplierCard', 'signup'],
  async ({ store, initialAppState, context }) => {
    const { query, params } = context;
    const { market } = initialAppState;
    const { dispatch } = store;
    const id = query.id as string;

    try {
      const { supplierId, numbers } = decodePublicSupplierId(id);

      if (numbers.length === 0) {
        throw new Error(`Wrong supplier id: ${supplierId}`);
      }

      const supplier = await fetchSupplierBasicData(supplierId).catch((error) => {
        dispatch(fetchSupplierError(error.status));
        if (error.status !== HttpStatusCode.OK) context.res.statusCode = error.status;
        throw error;
      });

      const translatedSupplier = translateRecursive(supplier, market.locale) as ISupplier;
      dispatch(saveSupplier(translatedSupplier));

      const supplierUrl = getSupplierUrl(translatedSupplier);

      const isValidUrlSlug =
        translatedSupplier.type?.[0] ===
        mapUrlSlugToSupplierType((params?.slug?.toString() || '') as UrlFriendlySlug);
      const isVenue = translatedSupplier.type?.[0] === 'venue';
      const seoVideo = translatedSupplier.seo?.video;
      const videoId = getSupplierVideoId(seoVideo);

      if (isEmpty(translatedSupplier) || !isValidUrlSlug || !supplierUrl) {
        return { notFound: true };
      }

      const profileRedirect = `/${market.prefix}${supplierUrl}`;

      if (!isVenue) {
        return { redirect: { permanent: true, destination: profileRedirect } };
      }

      if (!videoId) {
        return { redirect: { permanent: false, destination: profileRedirect } };
      }

      if (
        params &&
        typeof params.id === 'string' &&
        `/wedding-${params.slug}/${encodeURI(params.id)}` !== supplierUrl
      ) {
        return {
          redirect: {
            permanent: true,
            destination: `/${market.prefix}${supplierUrl}/video`,
          },
        };
      }

      const uiSupplier = buildUISupplier(translatedSupplier);
      const { name, town } = uiSupplier;
      const location = town || '';

      const canonicalUrl = getLocalisedCanonical(market, `${supplierUrl}/video`);
      dispatch(setCanonicalLink(canonicalUrl));
      dispatch(setAlternateLinks([]));

      const video: IVideoProps = {
        videoId,
        title: seoVideo?.title || '',
        description: seoVideo?.description || '',
        uploadDate: seoVideo?.uploadDate || '',
        duration: seoVideo?.duration || '',
        thumbnailUrl: getSupplierVideoThumbnail(seoVideo, videoId, 'maxresdefault'),
      };

      return {
        props: {
          canonicalUrl,
          profileUrl: supplierUrl,
          name,
          location,
          descriptionShort: translatedSupplier.description?.short || '',
          video,
        },
      };
    } catch (error) {
      SentryMinimal().captureException(error);
      return { notFound: true };
    }
  },
  {
    vercelCacheControl: {
      maxAge: EdgeCacheTTL.HOUR,
      sMaxAge: EdgeCacheTTL.HOUR,
      staleWhileRevalidate: EdgeCacheTTL.MONTH,
    },
    cloudflareCacheControl: {
      maxAge: EdgeCacheTTL.TEN_MINUTES,
      sMaxAge: EdgeCacheTTL.TEN_MINUTES,
      staleWhileRevalidate: EdgeCacheTTL.DAY,
    },
  },
);

VideoPage.AppType = AppType.Basic;

export default VideoPage;
